/**
 * AccountMenu Component (Client Island)
 *
 * Diamond Architecture: Client Component for interactivity.
 * - Auth-aware dropdown menu
 * - Opens auth modal when signed out user clicks protected items
 * - Uses AuthContext logout for proper state sync
 *
 * Every destination is a REAL ANCHOR (LET-83). The items used to be
 * `<DropdownMenuItem onClick={() => router.push(…)}>` — a navigation target
 * that existed only in JS, so the menu offered no middle-click, no
 * open-in-new-tab, no copy-link, and Enter reached a handler rather than a
 * link. Radix's `asChild` merges the item's role, focus and select wiring onto
 * the locale-aware `<Link>`, so keyboard navigation is unchanged and the
 * anchor is what activates. (The menu content is a portal that renders only
 * while open, so this is about native link affordances and the
 * `no-navigation-in-activation-handler` gate, not crawlability — the navbar
 * button beside it, `ListPropertyButton`, is the server-rendered one.)
 *
 * The signed-out "List property" item is auth-gated through the seam's
 * `gate`: with JS the modal opens in place; the anchor still points at the
 * real destination, which the auth middleware answers for with a redirect to
 * the sign-in modal — nobody is stranded on a dead control.
 */
import { useUnreadConversationsCount } from '@/shared/hooks/useUnreadConversationsCount';
import { EnhancedLink, Link } from '@/shared/i18n/routing';
import { useLocale, useTranslations } from '@/shared/i18n/useTranslations';
import { useAuthGate } from '@/shared/providers/AuthProvider';
import { useConfig } from '@/shared/providers/ConfigProvider';
import { avatarUrl as buildAvatarUrl } from '@letsgethome/lib/images';
import { formatBadgeCount } from '@letsgethome/lib/wasm';
import { routes } from '@letsgethome/routes';
import { plaintextString, s3Key } from '@letsgethome/types/branded';
import { Button } from '@letsgethome/ui/button';
import { ContactAvatar } from '@letsgethome/ui/contact-avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@letsgethome/ui/dropdown-menu';
import { Heart, HelpCircle, Home, LogOut, MessageSquare, Settings, User } from 'lucide-react';

export function AccountMenu() {
  const config = useConfig();
  const t = useTranslations('nav.account');
  const locale = useLocale();
  const { isAuthenticated, user, requireAuth, logout } = useAuthGate();
  const unreadConversationsCount = useUnreadConversationsCount();

  // The Messages badge — the Rust SSOT `formatBadgeCount` (0 → undefined →
  // no badge, >99 → "99+"), fed by its own `/api/conversations/unread-count`
  // query (R5: decoupled from auth hydration).
  const messagesBadge = formatBadgeCount(unreadConversationsCount);

  const handleLogout = () => logout();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative h-10 w-10 rounded-full">
          {isAuthenticated && user ? (
            <ContactAvatar
              locale={locale}
              givenName={user.givenName}
              familyName={user.familyName}
              avatarUrl={user.avatarKey ? buildAvatarUrl(s3Key(user.avatarKey), config) : undefined}
              identifier={user.id}
              size="sm"
            />
          ) : (
            <User className="h-5 w-5" />
          )}
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-56">
        {isAuthenticated ? (
          <>
            {/* Signed In Menu */}
            <DropdownMenuItem asChild>
              <Link href={routes.account.saved()}>
                <Heart className="mr-2 h-4 w-4" />
                {t('saved')}
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link href={routes.account.conversations.index()}>
                <MessageSquare className="mr-2 h-4 w-4" />
                {t('messages')}
                {messagesBadge !== undefined && (
                  <span className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1.5 text-xs font-medium text-white">
                    {plaintextString(messagesBadge)}
                  </span>
                )}
              </Link>
            </DropdownMenuItem>

            <DropdownMenuSeparator />

            <DropdownMenuItem asChild>
              <Link href={routes.account.listings.index()}>
                <Home className="mr-2 h-4 w-4" />
                {t('listings')}
              </Link>
            </DropdownMenuItem>

            <DropdownMenuSeparator />

            <DropdownMenuItem asChild>
              <Link href={routes.help()}>
                <HelpCircle className="mr-2 h-4 w-4" />
                {t('helpCenter')}
              </Link>
            </DropdownMenuItem>
            {/* Settings is BACK (PR-W2): /account/settings is a real web route
                again — the Rails-parity personal-info + notifications page. */}
            <DropdownMenuItem asChild>
              <Link href={routes.account.settings()}>
                <Settings className="mr-2 h-4 w-4" />
                {t('settings')}
              </Link>
            </DropdownMenuItem>

            <DropdownMenuSeparator />

            {/* Sign out is an ACTION, not a destination — it stays a menu item. */}
            <DropdownMenuItem onClick={handleLogout}>
              <LogOut className="mr-2 h-4 w-4" />
              {t('signOut')}
            </DropdownMenuItem>
          </>
        ) : (
          <>
            {/* Signed Out Menu */}
            <DropdownMenuItem onClick={() => requireAuth()}>
              <User className="mr-2 h-4 w-4" />
              {t('loginOrSignUp')}
            </DropdownMenuItem>

            <DropdownMenuSeparator />

            <DropdownMenuItem asChild>
              {/* `rel="nofollow"`: a real destination, but an auth-gated one a
                  crawler should not be invited into (robots.txt Disallows
                  `/account` anyway; this is the per-link statement of it). */}
              <EnhancedLink href={routes.account.listings.new()} gate={requireAuth} rel="nofollow">
                <Home className="mr-2 h-4 w-4" />
                {t('listProperty')}
              </EnhancedLink>
            </DropdownMenuItem>

            <DropdownMenuSeparator />

            <DropdownMenuItem asChild>
              <Link href={routes.help()}>
                <HelpCircle className="mr-2 h-4 w-4" />
                {t('helpCenter')}
              </Link>
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
